import { useMemo, useState, type JSX } from 'react';

import { Alert, Tabs } from '@bookora/ui';

import { PRIMARY_TAG } from '@/features/branches/copy';
import { useBranchContext } from '@/features/branches/hooks';

import { staffBranches, type StaffMember } from './api';
import { WorkingHoursEditor } from './working-hours-editor';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  A COLLEAGUE'S WEEK, ONE BRANCH AT A TIME
 * ════════════════════════════════════════════════════════════════════════════
 *
 * In a multi-branch clinic a doctor may work Saturdays at Vanak and Mondays at
 * Saadat-Abad. Each site's roster is its own grid, under a «شعبه» tab strip:
 * the grid reads `GET …/working-hours?branchId=` and saves a replace scoped to
 * that branch, so saving one site's week can never wipe the other's (the
 * per-staff replace used to be clinic-wide — multi-branch correction 4).
 * Overlapping the SAME person across two sites on one weekday is refused by
 * the server with its overlap sentence, shown on save.
 *
 * Tabs are the colleague's own branches (`StaffResponse.branches`) that the
 * viewer can see; until the response carries them, every branch the viewer
 * sees. Each tab keeps its own grid mounted, so switching tabs does not drop
 * an unsaved edit. A single-branch clinic gets the plain grid — no strip.
 */
export interface BranchWorkingHoursEditorProps {
  staffId: string;
  member?: StaffMember | null;
  caption?: string;
  /** Open on this branch (a `?branch=` deep link), when it is one of the tabs. */
  initialBranchId?: string | null;
}

export function BranchWorkingHoursEditor({
  staffId,
  member,
  caption,
  initialBranchId,
}: BranchWorkingHoursEditorProps): JSX.Element {
  const ctx = useBranchContext();

  const tabs = useMemo(() => {
    const own = member ? staffBranches(member) : null;
    if (!own) return ctx.branches;
    const visible = new Set(ctx.branches.map((b) => b.id));
    return own.filter((b) => visible.has(b.id));
  }, [ctx.branches, member]);

  const start =
    (initialBranchId && tabs.some((b) => b.id === initialBranchId) ? initialBranchId : null) ??
    (ctx.activeBranchId && tabs.some((b) => b.id === ctx.activeBranchId)
      ? ctx.activeBranchId
      : null) ??
    tabs[0]?.id ??
    null;
  // The user's pick once made; until then the start above (it may only be
  // known after `/branches/mine` answers, so it is not frozen into state).
  const [picked, setPicked] = useState<string | null>(null);
  const current = picked && tabs.some((b) => b.id === picked) ? picked : start;

  if (!ctx.isMulti) return <WorkingHoursEditor staffId={staffId} caption={caption} />;

  if (tabs.length === 0) {
    return (
      <Alert
        type="info"
        showIcon
        title="این کارمند در شعبه‌هایی که شما می‌بینید کار نمی‌کنه"
        description="شعبه‌های محل کار هر کارمند در «مشخصات» او تعیین می‌شه."
      />
    );
  }

  return (
    <Tabs
      aria-label="شعبه"
      size="small"
      activeKey={current ?? undefined}
      onChange={setPicked}
      items={tabs.map((branch) => ({
        key: branch.id,
        label: branch.isPrimary ? `${branch.name} (${PRIMARY_TAG})` : branch.name,
        children: <WorkingHoursEditor staffId={staffId} caption={caption} branchId={branch.id} />,
      }))}
    />
  );
}
